import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Search, Package, MapPin, Clock, User, 
  ArrowRight, RefreshCw, Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const USNMasterSearch = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [stats, setStats] = useState(null);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Please enter a USN code to search');
      return;
    }
    
    try {
      setSearching(true);
      setSearchResult(null);
      
      const response = await apiClient.get(`/usn-master/search?usn=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResult(response.data);
      
      if (!response.data.found) {
        toast.warning('USN not found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to search USN');
    } finally {
      setSearching(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/usn-master/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp || timestamp === 'Unknown') return 'Unknown';
    try {
      return new Date(timestamp).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/admin/dashboard')} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">USN Master</h1>
              <p className="text-sm text-[#E8DCC8]">Track USN movement across sections</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchStats}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Database className="w-4 h-4 mr-2" />
            View Stats
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8">
        {/* Search Section */}
        <Card className="border-[#E5DFD0] mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-[#2D5F4F]" />
              Search USN
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter USN code (e.g., USN000001/25-26)"
                className="font-mono"
                autoFocus
                data-testid="usn-search-input"
              />
              <Button 
                onClick={handleSearch}
                disabled={searching}
                className="bg-[#2D5F4F] hover:bg-[#1A1F2E]"
                data-testid="search-button"
              >
                {searching ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats Section */}
        {stats && (
          <Card className="border-[#E5DFD0] mb-6">
            <CardHeader>
              <CardTitle className="text-sm">USN Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-[#2D5F4F]/10 rounded">
                  <p className="text-2xl font-bold text-[#2D5F4F]">{stats.total_usns?.toLocaleString() || 0}</p>
                  <p className="text-xs text-gray-500">Total USNs Tracked</p>
                </div>
                {Object.entries(stats.section_wise || {}).slice(0, 3).map(([section, count]) => (
                  <div key={section} className="text-center p-3 bg-gray-50 rounded">
                    <p className="text-xl font-bold">{count?.toLocaleString() || 0}</p>
                    <p className="text-xs text-gray-500 truncate">{section}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Result */}
        {searchResult && (
          <Card className={`border-2 ${searchResult.found ? 'border-[#2D5F4F]' : 'border-red-300'}`}>
            <CardHeader className={searchResult.found ? 'bg-[#2D5F4F]/10' : 'bg-red-50'}>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  {searchResult.found ? (
                    <Package className="w-5 h-5 text-[#2D5F4F]" />
                  ) : (
                    <Package className="w-5 h-5 text-red-500" />
                  )}
                  {searchResult.found ? 'USN Found' : 'USN Not Found'}
                </span>
                {searchResult.source && (
                  <Badge variant="outline">{searchResult.source}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            
            {searchResult.found && searchResult.usn && (
              <CardContent className="pt-6">
                {/* USN Details */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <p className="text-xs text-gray-500">USN Code</p>
                    <p className="font-mono font-bold text-lg">{searchResult.usn.usn_code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Bundle Barcode</p>
                    <p className="font-mono font-medium">{searchResult.usn.bundle_barcode}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Section</p>
                    <Badge className="bg-[#2D5F4F]">{searchResult.usn.current_section}</Badge>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Current Status</p>
                    <p className="font-medium">{searchResult.usn.current_status}</p>
                  </div>
                  {searchResult.usn.design_name && (
                    <div>
                      <p className="text-xs text-gray-500">Design</p>
                      <p className="font-medium">{searchResult.usn.design_name}</p>
                    </div>
                  )}
                  {searchResult.usn.sort_no && (
                    <div>
                      <p className="text-xs text-gray-500">Sort No</p>
                      <p className="font-medium">{searchResult.usn.sort_no}</p>
                    </div>
                  )}
                </div>

                {/* Movement History */}
                {searchResult.usn.movement_history && searchResult.usn.movement_history.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Movement History
                    </h4>
                    <div className="space-y-2">
                      {searchResult.usn.movement_history.map((movement, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-[#2D5F4F]/20 flex items-center justify-center">
                              <span className="text-xs font-bold text-[#2D5F4F]">{idx + 1}</span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline">{movement.section}</Badge>
                              <ArrowRight className="w-3 h-3 text-gray-400" />
                              <span className="font-medium text-sm">{movement.operation}</span>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTimestamp(movement.timestamp)}
                              </span>
                              {movement.staff_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {movement.staff_name}
                                </span>
                              )}
                              {movement.transfer_id && (
                                <span className="flex items-center gap-1">
                                  <Package className="w-3 h-3" />
                                  {movement.transfer_id}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
            
            {!searchResult.found && (
              <CardContent className="py-8 text-center text-gray-500">
                <p>{searchResult.message || 'The USN code was not found in any section.'}</p>
                <p className="text-sm mt-2">Please verify the USN code and try again.</p>
              </CardContent>
            )}
          </Card>
        )}
      </main>
    </div>
  );
};

export default USNMasterSearch;
